<template>
  <BannerComponent />
  <section class="py-10 md:py-20">
    <AnimationComponent>
      <div
        v-if="!shopsStore.loading && shopsStore.featuredShops.length > 0"
        class="max-sm:px-6 max-xl:px-13 mb-12"
      >
        <div class="max-w-content mx-auto flex flex-col items-center md:flex-row md:justify-between gap-8">
          <h2 class="text-4xl font-semibold">Featured Stores</h2>
          <router-link :to="`/${themeStore.current}/stores`">
            <Button size="lg">View All</Button>
          </router-link>
        </div>
      </div>
      <FeaturedStoreCarousel :no-horizontal-padding="false" />
      <MerchantLogoCarousel :hideText="true" />
    </AnimationComponent>
  </section>
  <section class="lg:px-13 py-10 md:py-20">
    <AnimationComponent>
      <div class="max-w-content mx-auto">
      <div class="max-sm:px-6 max-xl:px-13 mb-14 text-center max-w-2xl mx-auto">
        <h2 class="text-4xl font-semibold md:mb-5">
          {{ productInfo?.title }}
        </h2>
        <p class="text-xl text-gray-600 max-md:pt-12">
          {{ productInfo?.subTitle }}
        </p>
      </div>

      <InfoCardsCarousel
        :cards="productInfo?.cards || []"
        icon-bg-class="bg-primary"
        icon-text-class="text-white"
        title-class="text-2xl font-semibold"
        description-class="text-lg md:text-xl"
      />
      </div>
    </AnimationComponent>
  </section>

  <section class="max-sm:px-6 max-xl:px-13 py-10 md:py-20">
    <AnimationComponent>
      <div class="max-w-content mx-auto">
        <HowItWorksTabs id="how-it-works" :tabs="tabs" />
      </div>
    </AnimationComponent>
  </section>
  <section class="max-sm:px-0 max-xl:px-13 py-10 md:py-20">
    <AnimationComponent>
      <div class="max-w-content mx-auto">
        <GetStarted :title="getStartedCta.title" :description="getStartedCta.description" />
      </div>
    </AnimationComponent>
  </section>
  <section class="max-sm:px-6 max-xl:px-13 pt-10 md:pt-20 pb-10">
    <AnimationComponent>
      <div class="max-w-content mx-auto">
        <ShopNow
          :title="shopNowCta.title"
          :description="shopNowCta.description"
          :image="shopNowCta.image"
          :backgroundImage="shopNowCta.backgroundImage"
          :backgroundImageMobile="shopNowCta.backgroundImageMobile"
        />
      </div>
    </AnimationComponent>
  </section>
  <section class="max-sm:px-0 max-xl:px-13 py-10 md:py-20">
    <AnimationComponent>
      <div class="max-w-content mx-auto">
        <NewsletterSubscribe />
      </div>
    </AnimationComponent>
  </section>
  <section class="mx-auto max-w-5xl py-10 md:py-20 max-sm:px-6 max-lg:px-13 lg:px-24">
    <AnimationComponent>
      <div class="max-w-content mx-auto">
        <FaqGroup v-if="faqData" :items="faqData.items" />
      </div>
    </AnimationComponent>
  </section>
  <section class="max-sm:px-6 max-xl:px-13 py-10 md:py-20">
    <AnimationComponent>
      <div class="max-w-content mx-auto">
        <GetInTouch />
      </div>
    </AnimationComponent>
  </section>
  <LatestBlogs :blog-tag-id="SHOPPER_BLOG_TAG_ID" :remove-desktop-padding-top="false" />
  <section>
    <AnimationComponent>
      <SignUp />
    </AnimationComponent>
  </section>
</template>

<script setup lang="ts">
import AnimationComponent from '@/components/AnimationComponent.vue';
import BannerComponent from '@/components/BannerComponent.vue';
import GetInTouch from '@/components/call-to-actions/GetInTouch.vue';
import GetStarted from '@/components/call-to-actions/GetStarted.vue';
import NewsletterSubscribe from '@/components/call-to-actions/NewsletterSubscribe.vue';
import ShopNow from '@/components/call-to-actions/ShopNow.vue';
import SignUp from '@/components/call-to-actions/SignUp.vue';
import FaqGroup from '@/components/FaqComponent.vue';
import FeaturedStoreCarousel from '@/components/FeaturedStoreCarousel.vue';
import HowItWorksTabs from '@/components/HowItWorksTabs.vue';
import InfoCardsCarousel from '@/components/InfoCardsCarousel.vue';
import MerchantLogoCarousel from '@/components/MerchantLogoCarousel.vue';
import { Button } from '@/components/ui/button';
import GetStartedCtaJsonData from '@/data/get-started-cta.json';
import HowItWorksJsonData from '@/data/how-it-works-tabs.json';
import InfoCardsCarouselJsonData from '@/data/info-cards-carousel.json';
import questions from '@/data/questions.json';
import ShopNowCtaJsonData from '@/data/shop-now-cta.json';
import { useShopsStore } from '@/stores/shop';
import { useThemeStore } from '@/stores/theme';
import { computed, onMounted } from 'vue';
import LatestBlogs from '@/common/LatestBlogs.vue';
import { SHOPPER_BLOG_TAG_ID } from '@/config.ts';

const themeStore = useThemeStore();
const shopsStore = useShopsStore();

const tabs = computed(() => HowItWorksJsonData.find((item) => item.category === themeStore.current)?.tabs || []);
const productInfo = computed(
  () => InfoCardsCarouselJsonData.find((item) => item.category === themeStore.current) || null,
);
const getStartedCta = computed(
  () =>
    GetStartedCtaJsonData.find((item) => item.category === themeStore.current)?.cta || { title: '', description: '' },
);
const shopNowCta = computed(
  () =>
    ShopNowCtaJsonData.find((item) => item.category === themeStore.current)?.cta || {
      title: '',
      description: '',
      image: '',
      backgroundImage: '',
      backgroundImageMobile: '',
    },
);
const faqData = computed(() => {
  return questions.find((c) => c.category === themeStore.current);
});

onMounted(async () => {
  await shopsStore.fetchStores();
});
</script>
